const DEFAULT_FILE_NAME = "filename.xls";
const DEFAULT_MIME_TYPE = "application/octet-stream";

function normalizeFileNamePart(value) {
  if (value === undefined || value === null) return "";

  const text = String(value)
    .replace(/<[^>]*>/g, "")
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, " ")
    .trim();

  return text.replace(/^[_\-\s]+|[_\-\s]+$/g, "");
}

/**
 * Build a stable report/document filename from the context that is available.
 * Missing address, chain, or currency values are omitted instead of rendering
 * `undefined` into the downloaded filename.
 */
export function buildDownloadFileName({
  address,
  identifier,
  blockchain,
  currency,
  name,
  fallbackName = "报告",
  extension = "",
} = {}) {
  const parts = [
    address || identifier,
    blockchain,
    currency,
    name || fallbackName,
  ]
    .map(normalizeFileNamePart)
    .filter(Boolean);
  const suffix = extension ? `.${String(extension).replace(/^\./, "")}` : "";

  return `${parts.join("_") || normalizeFileNamePart(fallbackName) || "报告"}${suffix}`;
}

export function downloadBlob(
  data,
  fileName = DEFAULT_FILE_NAME,
  type = DEFAULT_MIME_TYPE,
) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  const hasBlob = typeof Blob !== "undefined";
  if (!hasBlob) {
    return false;
  }

  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const filename = fileName || DEFAULT_FILE_NAME;

  if (typeof window.navigator.msSaveOrOpenBlob === "function") {
    window.navigator.msSaveOrOpenBlob(blob, filename);
    return true;
  }

  if (typeof window.navigator.msSaveBlob === "function") {
    window.navigator.msSaveBlob(blob, filename);
    return true;
  }

  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  try {
    link.style.display = "none";
    link.href = blobUrl;
    link.setAttribute("download", filename);
    if (typeof link.download === "undefined") {
      link.setAttribute("target", "_blank");
    }
    document.body.appendChild(link);
    link.click();
    return true;
  } finally {
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
    window.URL.revokeObjectURL(blobUrl);
  }
}

export function downloadUrl(url, fileName = DEFAULT_FILE_NAME) {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    !url
  ) {
    return false;
  }

  const link = document.createElement("a");
  link.style.display = "none";
  link.href = url;
  link.setAttribute("download", fileName || DEFAULT_FILE_NAME);
  if (typeof link.download === "undefined") {
    link.setAttribute("target", "_blank");
  }

  document.body.appendChild(link);
  try {
    link.click();
    return true;
  } finally {
    document.body.removeChild(link);
  }
}
